import type { ReactNode } from 'react';
import {
  CheckCircleOutlined,
  CommentOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  RobotOutlined,
  StopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { Bento, StatTile, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { KHONG_CO, soVN } from '../../../../lib/dinh-dang';
import { tenVaiTuDanhSach, useVaiTroToChuc } from '../../hooks/useChung';
import { useSuDung } from '../../hooks/useVanHanh';
import { beRongThanh, phanTram, thoiGianDoc } from '../../nghiep-vu/van-hanh';
import type { SuDung as SoLieuSuDung } from '../../types';

/** Thời gian cho người đọc — xem `thoiGianDoc`. */
function useThoiGian(): (ms: number | null) => string {
  const { t, locale } = useT();
  return (ms) => {
    const tg = thoiGianDoc(ms);
    if (!tg) return KHONG_CO;
    return tg.donVi === 'ms'
      ? t('troLyAiVanHanhSuDung.ms', { so: soVN(tg.so, locale) })
      : t('troLyAiVanHanhSuDung.giay', { so: soVN(tg.so, locale) });
  };
}

/** Một dòng có thanh tỉ lệ: tên · thanh · số. Thanh so với dòng CAO NHẤT trong khối, không với tổng. */
function Dong({
  ten, n, dinh, ghi, tone = 'primary',
}: {
  ten: string;
  n: number;
  dinh: number;
  ghi?: string;
  tone?: 'primary' | 'violet';
}) {
  const { locale } = useT();
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-content-primary">{ten}</span>
        {ghi ? <span className="shrink-0 text-xs text-warning-700">{ghi}</span> : null}
        <span className="shrink-0 text-sm font-semibold tabular-nums text-content-primary">{soVN(n, locale)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-subtle">
        <div
          className={cn('h-full rounded-full', tone === 'violet' ? 'bg-violet-500' : 'bg-primary-500')}
          // Bề rộng là DỮ LIỆU, không phải một bậc trong thang thiết kế — nên đi qua `style`.
          style={{ width: `${beRongThanh(n, dinh)}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function Trong({ loi }: { loi: string }) {
  return <p className="py-2 text-sm text-content-muted">{loi}</p>;
}

/** Bốn con số đọc trong một cái liếc. */
export function ThongSo({ d }: { d: SoLieuSuDung }) {
  const { t, locale } = useT();
  const l = d.luotHoi;
  const p = phanTram(l.ok, l.tong);
  return (
    <>
      <StatTile
        icon={<MessageOutlined />}
        tone="primary"
        nhan={t('troLyAiVanHanhSuDung.luotHoi')}
        so={soVN(l.tong, locale)}
        sub={t('troLyAiVanHanhSuDung.trongKy', { n: d.ngay })}
      />
      <StatTile
        icon={<CheckCircleOutlined />}
        tone="success"
        nhan={t('troLyAiVanHanhSuDung.traLoiDuoc')}
        so={soVN(l.ok, locale)}
        sub={t('troLyAiVanHanhSuDung.tyLe', { p: p === null ? KHONG_CO : `${soVN(p, locale)}%` })}
      />
      {/* "Bị từ chối" KHÔNG phải lỗi: cổng chặn một câu hỏi ngoài quyền là nó đang làm đúng việc. Tô đỏ như "Gặp lỗi"
          sẽ dạy người vận hành đi tắt đúng thứ đang bảo vệ họ. */}
      <StatTile
        icon={<StopOutlined />}
        tone="warning"
        nhan={t('troLyAiVanHanhSuDung.biTuChoi')}
        so={soVN(l.biTuChoi, locale)}
        sub={t('troLyAiVanHanhSuDung.biTuChoiMo')}
      />
      {/* Lượt hỏng không thành tin nhắn nên KHÔNG nằm trong "Lượt hỏi" — chia cho tổng ấy ra một tỉ lệ vô nghĩa. */}
      <StatTile
        icon={<ExclamationCircleOutlined />}
        tone={l.hong ? 'danger' : 'neutral'}
        nhan={t('troLyAiVanHanhSuDung.gapLoi')}
        so={soVN(l.hong, locale)}
        sub={l.hong ? t('troLyAiVanHanhSuDung.gapLoiMo') : t('troLyAiVanHanhSuDung.khongCoLoi')}
      />
    </>
  );
}

/** Tốc độ trả lời — hai mốc (p50, p95), không phải một trung bình. */
export function TocDo({ d, span }: { d: SoLieuSuDung; span?: string }) {
  const { t } = useT();
  const thoiGian = useThoiGian();
  return (
    <Tile span={span ?? 'lg:col-span-4'}>
      <TileHeader icon={<ThunderboltOutlined />} tone="orange" title={t('troLyAiVanHanhSuDung.tocDo')} />
      <TileBody className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-subtle px-3 py-2.5">
            <p className="text-xs text-content-secondary">{t('troLyAiVanHanhSuDung.thuong')}</p>
            <p className="mt-0.5 text-xl font-semibold tabular-nums text-content-primary">
              {thoiGian(d.luotHoi.p50Ms)}
            </p>
          </div>
          <div className="rounded-lg bg-subtle px-3 py-2.5">
            <p className="text-xs text-content-secondary">{t('troLyAiVanHanhSuDung.chamNhat')}</p>
            <p className="mt-0.5 text-xl font-semibold tabular-nums text-content-primary">
              {thoiGian(d.luotHoi.p95Ms)}
            </p>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-content-muted">{t('troLyAiVanHanhSuDung.tocDoMo')}</p>
      </TileBody>
    </Tile>
  );
}

/** Nguồn dữ liệu được hỏi nhiều nhất. */
export function TheoNguon({ d, span }: { d: SoLieuSuDung; span?: string }) {
  const { t, locale } = useT();
  const dinh = Math.max(1, ...d.theoNguon.map((x) => x.n));
  return (
    <Tile span={span ?? 'lg:col-span-6'}>
      <TileHeader
        icon={<DatabaseOutlined />}
        tone="teal"
        title={t('troLyAiVanHanhSuDung.theoNguon')}
        mo={t('troLyAiVanHanhSuDung.theoNguonMo')}
      />
      <TileBody className="space-y-3">
        {d.theoNguon.length === 0 ? (
          <Trong loi={t('troLyAiVanHanhSuDung.chuaCoNguon')} />
        ) : (
          d.theoNguon.map((x) => (
            <Dong
              key={x.nguon}
              ten={x.ten}
              n={x.n}
              dinh={dinh}
              ghi={x.khongOk ? t('troLyAiVanHanhSuDung.chuaTraLoiDuoc', { n: soVN(x.khongOk, locale) }) : undefined}
            />
          ))
        )}
      </TileBody>
    </Tile>
  );
}

/** Ai đang hỏi — theo TÊN vai trò của tổ chức, không theo mã vai mà nhật ký ghi. */
export function TheoVai({ d, span }: { d: SoLieuSuDung; span?: string }) {
  const { t } = useT();
  const vai = useVaiTroToChuc();
  const dinh = Math.max(1, ...d.theoVai.map((x) => x.n));
  return (
    <Tile span={span ?? 'lg:col-span-6'}>
      <TileHeader
        icon={<TeamOutlined />}
        tone="violet"
        title={t('troLyAiVanHanhSuDung.theoVai')}
        mo={t('troLyAiVanHanhSuDung.theoVaiMo')}
      />
      <TileBody className="space-y-3">
        {d.theoVai.length === 0 ? (
          <Trong loi={t('troLyAiVanHanhSuDung.chuaCoVai')} />
        ) : (
          d.theoVai.map((x) => (
            <Dong key={x.vai} ten={tenVaiTuDanhSach(x.vai, vai.data)} n={x.n} dinh={dinh} tone="violet" />
          ))
        )}
      </TileBody>
    </Tile>
  );
}

function NhomTroChuyen({
  icon, ten, muc,
}: {
  icon: ReactNode;
  ten: string;
  muc: { id: string; nhan: string; doan: number; luot: number }[];
}) {
  const { t, locale } = useT();
  return (
    <div className="rounded-lg border border-default p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-content-secondary">
        <span className="text-content-muted" aria-hidden>{icon}</span>
        {ten}
      </p>
      {muc.length === 0 ? (
        <p className="text-sm text-content-muted">{t('troLyAiVanHanhSuDung.chuaCoDoan')}</p>
      ) : (
        <ul className="space-y-1.5">
          {muc.map((m) => (
            <li key={m.id} className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate text-sm text-content-primary">{m.nhan}</span>
              <span className="shrink-0 text-xs tabular-nums text-content-secondary">
                {t('troLyAiVanHanhSuDung.doanLuot', { doan: soVN(m.doan, locale), luot: soVN(m.luot, locale) })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Trò chuyện đã lưu. Khối trên đếm LƯỢT HỎI, khối này đếm CUỘC TRÒ CHUYỆN — ghép hai thứ thành một con số "lượt dùng"
 * là tạo ra một đại lượng không ai nói được nó đo gì, nên tiêu đề và đơn vị nói thẳng ra.
 */
export function TroChuyen({ d, span }: { d: SoLieuSuDung; span?: string }) {
  const { t } = useT();
  const troLy = d.hoiThoai.filter((h) => h.loai === 'tro_ly');
  const bots = d.hoiThoai.filter((h) => h.loai === 'chatbot');
  return (
    <Tile span={span ?? 'lg:col-span-6'}>
      <TileHeader
        icon={<CommentOutlined />}
        tone="sky"
        title={t('troLyAiVanHanhSuDung.hoiThoaiDaLuu')}
        mo={t('troLyAiVanHanhSuDung.hoiThoaiMo')}
      />
      <TileBody className="grid gap-3 sm:grid-cols-2">
        <NhomTroChuyen icon={<MessageOutlined />} ten={t('troLyAiVanHanhSuDung.troLyAi')} muc={troLy} />
        <NhomTroChuyen icon={<RobotOutlined />} ten={t('troLyAiVanHanhSuDung.chatbot')} muc={bots} />
      </TileBody>
    </Tile>
  );
}

/** Những thứ số liệu KHÔNG nói được — chỉ hiện khi máy chủ có gửi. Câu do máy chủ soạn, không dịch. */
export function ChuaDoDuoc({ d }: { d: SoLieuSuDung }) {
  if (d.khongDoDuoc.length === 0) return null;
  return (
    <Tile>
      <TileBody className="space-y-1.5">
        {d.khongDoDuoc.map((c) => (
          <p key={c} className="text-xs leading-relaxed text-content-secondary">
            {c}
          </p>
        ))}
      </TileBody>
    </Tile>
  );
}

export function KhungCho({ n = 4 }: { n?: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <Tile key={i} span="sm:col-span-6 lg:col-span-3" aria-busy="true">
          <Skeleton className="h-[76px] w-full" />
        </Tile>
      ))}
    </>
  );
}

/**
 * Khối sử dụng gọn cho màn Nhật ký: bốn con số, tốc độ, nguồn và vai. Màn Tổng quan tự xếp từng mảnh vào lưới của nó —
 * cùng dữ liệu, khác bố cục.
 */
export function SuDung({ ngay = 7 }: { ngay?: number }) {
  const { t } = useT();
  const { data, isPending, error } = useSuDung(ngay);

  if (isPending || !data) {
    return (
      <Bento>
        {error ? (
          <Tile>
            <TileBody>
              <p className="text-sm text-content-secondary">{t('troLyAiVanHanhSuDung.chuaLayDuoc')}</p>
            </TileBody>
          </Tile>
        ) : (
          <KhungCho />
        )}
      </Bento>
    );
  }

  return (
    <Bento>
      <ThongSo d={data} />
      <TocDo d={data} span="lg:col-span-4" />
      <TheoNguon d={data} span="lg:col-span-4" />
      <TheoVai d={data} span="lg:col-span-4" />
    </Bento>
  );
}
